import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import { Search as SearchIcon, X } from "lucide-react";

export default function Explore() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [taxonomies, setTax] = useState({ rooms: [], styles: [], color_tones: [], luxury_levels: [] });
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(params.get("search") || "");

    const filters = useMemo(() => ({
        room: params.get("room") || "",
        style: params.get("style") || "",
        company: params.get("company") || "",
        color: params.get("color") || "",
        luxury_min: params.get("luxury_min") || "",
        featured: params.get("featured") === "true" ? true : undefined,
    }), [params]);

    useEffect(() => {
        Promise.all([api.get("/taxonomies"), api.get("/companies")]).then(([t, c]) => {
            setTax(t.data);
            setCompanies(c.data || []);
        });
    }, []);

    useEffect(() => {
        const ac = new AbortController();
        setLoading(true);
        const q = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v !== "" && v !== undefined && v !== null) q[k] = v;
        }
        if (search) q.search = search;
        q.limit = 60;
        api.get("/projects", { params: q, signal: ac.signal })
            .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
            .catch(() => {})
            .finally(() => setLoading(false));
        return () => ac.abort();
    }, [filters, search]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const clearAll = () => {
        setParams({});
        setSearch("");
    };

    const activeCount = Object.values(filters).filter((v) => v !== "" && v !== undefined).length + (search ? 1 : 0);

    return (
        <main data-testid="explore-page" className="bg-black min-h-screen pt-32 pb-24">
            <div className="container-luxury">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="overline mb-4">Inspiration Explorer</div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[0.95]">Browse the entire showroom.</h1>
                    </div>
                    <div className="text-xs tracking-[0.3em] uppercase text-white/50">{total} projects</div>
                </div>

                {/* Search */}
                <div className="relative mb-10">
                    <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search projects, brands, locations…"
                        data-testid="explore-search"
                        className="w-full bg-[#0a0a0a] border border-white/15 pl-12 pr-4 py-4 text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
                    {/* Sidebar filters */}
                    <aside className="space-y-8" data-testid="filter-sidebar">
                        <FilterGroup title="Room" options={taxonomies.rooms} value={filters.room} onChange={(v) => setFilter("room", v)} testKey="room" />
                        <FilterGroup title="Style" options={taxonomies.styles} value={filters.style} onChange={(v) => setFilter("style", v)} testKey="style" />
                        <FilterGroup
                            title="Company"
                            options={companies.map((c) => ({ label: c.name, value: c.slug }))}
                            value={filters.company}
                            onChange={(v) => setFilter("company", v)}
                            testKey="company"
                        />
                        <FilterGroup title="Color Tone" options={taxonomies.color_tones} value={filters.color} onChange={(v) => setFilter("color", v)} testKey="color" />
                        <FilterGroup
                            title="Luxury Level"
                            options={[{ label: "★ 3+", value: "3" }, { label: "★ 4+", value: "4" }, { label: "★ 5", value: "5" }]}
                            value={filters.luxury_min}
                            onChange={(v) => setFilter("luxury_min", v)}
                            testKey="luxury"
                        />

                        {activeCount > 0 && (
                            <button data-testid="clear-filters" onClick={clearAll} className="text-xs tracking-[0.3em] uppercase text-gold hover:text-gold-soft inline-flex items-center gap-2">
                                <X size={14} /> Clear all filters
                            </button>
                        )}
                    </aside>

                    <div>
                        {loading ? (
                            <div className="masonry">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} className="bg-[#0a0a0a] animate-pulse" style={{ height: 200 + ((i * 47) % 240) }} />
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-32 text-white/50" data-testid="explore-empty">
                                <div className="font-serif text-3xl mb-3">Nothing matches yet.</div>
                                <p className="text-sm">Try removing a filter or two.</p>
                            </div>
                        ) : (
                            <div className="masonry" data-testid="explore-results">
                                {items.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

function FilterGroup({ title, options, value, onChange, testKey }) {
    const opts = options.map((o) => (typeof o === "string" ? { label: o, value: o } : o));
    return (
        <div>
            <div className="overline mb-4">{title}</div>
            <div className="flex flex-wrap gap-2" data-testid={`filter-group-${testKey}`}>
                {opts.map((o) => {
                    const active = value === o.value;
                    return (
                        <button
                            key={o.value}
                            onClick={() => onChange(active ? "" : o.value)}
                            data-testid={`filter-${testKey}-${o.value}`}
                            className={`text-xs tracking-wide px-3 py-1.5 border transition-colors duration-300 ${
                                active ? "bg-gold text-black border-gold" : "bg-transparent text-white/70 border-white/15 hover:border-gold hover:text-gold"
                            }`}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
